import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, Banknote, BookOpen, Inbox, Mail, PackageCheck, Receipt, Timer } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { DonutChart } from '@/components/charts/DonutChart';
import { AnimatedNumber } from '@/components/charts/AnimatedNumber';
import { DataView, downloadCsv } from '@/components/data/DataView';
import { useFilters, rangeISO } from '@/state/filters';
import {
  useOrderSummary,
  useProductSales,
  type ProductSalesRow,
} from '@/api/legacy/reports';
import { useTrialBalance } from '@/api/bi';
import { SnapshotMenu } from '@/components/system/SnapshotMenu';
import { ScheduledReportsCard } from '@/pages/reports/ScheduledReportsCard';
import { useAccess } from '@/features/AccessProvider';
import { fmt } from '@/lib/format';
import { statusLabel } from '@/lib/status';
import { cn } from '@/lib/cn';

/**
 * Reports — Day Close + Sales Register.
 *
 * Stage 1 polish:
 *   - Animated headline KPIs that ride BI-owned report endpoints so staff
 *     APIs remain isolated from BI access control.
 *   - Status counts use the shared `statusLabel` taxonomy so labels match
 *     the Live Ops / Orders pages exactly.
 *   - Sales Register table now uses `<DataView>`: search, sort, server
 *     pagination, CSV export — all from one component.
 *   - Per the cursor rule: we explicitly reuse `useOrderSummary` and
 *     `useProductSales` rather than minting new endpoints.
 */
export function ReportsPage() {
  const { range, storeId } = useFilters();
  const access = useAccess();
  const params = useMemo(
    () => ({
      from: format(range.from, 'yyyy-MM-dd'),
      to: format(range.to, 'yyyy-MM-dd'),
      storeId,
    }),
    [range, storeId],
  );
  const tabs = useMemo(
    () => [
      { id: 'day-close', resource: 'reports.day-close', label: 'Day Close' },
      { id: 'sales-register', resource: 'reports.sales-register', label: 'Sales Register' },
      { id: 'trial-balance', resource: 'reports.trial-balance', label: 'Trial Balance', icon: <BookOpen className="mr-1.5 h-3.5 w-3.5" /> },
      { id: 'schedules', resource: 'reports.schedules', label: 'Schedules', icon: <Mail className="mr-1.5 h-3.5 w-3.5" /> },
    ].filter((tab) => access.can(tab.resource)),
    [access],
  );
  const [activeTab, setActiveTab] = useState<string | null>(null);

  useEffect(() => {
    if (tabs.length === 0) {
      setActiveTab(null);
      return;
    }
    if (!activeTab || !tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [activeTab, tabs]);

  if (tabs.length === 0) {
    return (
      <>
        <PageHeader
          eyebrow="Reports"
          title="Reports"
          description="No report tabs are assigned to your account yet."
        />
        <EmptyState
          icon={<Inbox className="h-5 w-5" />}
          title="No report access"
          description="Ask a superuser to assign the report tabs you need."
          withTip
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Reports"
        title="Day Close, Sales & Trial Balance"
        description="Authoritative end-of-day numbers from the same endpoints your accountants use."
        actions={access.can('reports.snapshot', 'export') ? <SnapshotMenu filename="reports" /> : undefined}
      />

      <Tabs value={activeTab ?? tabs[0].id} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id}>
              {'icon' in tab ? tab.icon : null}{tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {access.can('reports.day-close') ? (
          <TabsContent value="day-close">
            <DayCloseSection params={params} />
          </TabsContent>
        ) : null}

        {access.can('reports.sales-register') ? (
          <TabsContent value="sales-register">
            <SalesRegisterSection params={params} />
          </TabsContent>
        ) : null}

        {access.can('reports.trial-balance') ? (
          <TabsContent value="trial-balance">
            <TrialBalanceSection />
          </TabsContent>
        ) : null}

        {access.can('reports.schedules') ? (
          <TabsContent value="schedules">
            <ScheduledReportsCard canManage={access.can('reports.schedules.manage', 'manage')} />
          </TabsContent>
        ) : null}
      </Tabs>
    </>
  );
}

function TrialBalanceSection() {
  const access = useAccess();
  if (!access.can('reports.trial-balance.summary-cards') && !access.can('reports.trial-balance.reconciliation-table')) {
    return <EmptyState title="No trial balance widgets assigned" />;
  }
  return <TrialBalanceData />;
}

function TrialBalanceData() {
  const access = useAccess();
  const range = useFilters((s) => s.range);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const storeId = useFilters((s) => s.storeId);
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const tb = useTrialBalance({ start: iso.start, end: iso.end, storeId });

  if (tb.isLoading) return <Skeleton className="h-72 w-full" />;
  if (tb.isError) return <ErrorState onRetry={() => void tb.refetch()} />;
  const data = tb.data;
  if (!data || data.rows.length === 0) {
    return (
      <EmptyState
        icon={<Inbox className="h-5 w-5" />}
        title="No completed orders or closes in this window"
        description="Pick a longer range or a different store to see the reconciliation."
        withTip
      />
    );
  }
  const t = data.totals;
  const varianceTone = Math.abs(t.variance_cash + t.variance_online) < 1
    ? 'success'
    : Math.abs(t.variance_pct) > 0.02
      ? 'danger'
      : 'warning';
  return (
    <div className="space-y-4">
      {access.can('reports.trial-balance.summary-cards') ? (
        <div className="grid gap-4 stagger md:grid-cols-2 lg:grid-cols-4">
          <SummaryStat label="Gross (orders)" value={t.gross} format={(n) => fmt.npr(n)} icon={<Banknote className="h-4 w-4" />} />
          <SummaryStat label="Cash (orders)" value={t.cash} format={(n) => fmt.npr(n)} />
          <SummaryStat label="Online (orders)" value={t.online} format={(n) => fmt.npr(n)} />
          <SummaryStat label="Stores reporting" value={t.stores} format={(n) => fmt.number(n)} />
        </div>
      ) : null}
      {access.can('reports.trial-balance.reconciliation-table') ? <Card>
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-2">
            <BookOpen className="h-4 w-4" /> Reconciliation
          </CardTitle>
          <Badge tone={varianceTone}>
            {varianceTone === 'success' ? 'in balance' : `${fmt.npr(Math.abs(t.variance_cash + t.variance_online))} variance`}
          </Badge>
        </CardHeader>
        <CardBody className="!pt-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[840px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Store</th>
                  <th className="px-3 py-2 text-right font-medium">Orders</th>
                  <th className="px-3 py-2 text-right font-medium">Cash (orders)</th>
                  <th className="px-3 py-2 text-right font-medium">Cash (declared)</th>
                  <th className="px-3 py-2 text-right font-medium">Online (orders)</th>
                  <th className="px-3 py-2 text-right font-medium">Online (declared)</th>
                  <th className="px-3 py-2 text-right font-medium">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.rows.map((row) => {
                  const totalVar = row.variance_cash + row.variance_online;
                  const tone = Math.abs(totalVar) < 1 ? 'text-success' : Math.abs(totalVar) > row.gross * 0.02 ? 'text-danger' : 'text-warning-soft';
                  return (
                    <tr key={row.store_id}>
                      <td className="px-3 py-2 font-medium">{row.store_name}</td>
                      <td className="px-3 py-2 text-right kpi-num">{fmt.number(row.orders)}</td>
                      <td className="px-3 py-2 text-right kpi-num">{fmt.npr(row.cash)}</td>
                      <td className="px-3 py-2 text-right kpi-num">{fmt.npr(row.declared_cash + row.extra_cash)}</td>
                      <td className="px-3 py-2 text-right kpi-num">{fmt.npr(row.online)}</td>
                      <td className="px-3 py-2 text-right kpi-num">{fmt.npr(row.declared_online)}</td>
                      <td className={cn('px-3 py-2 text-right kpi-num', tone)}>
                        {Math.abs(totalVar) >= 1 ? (
                          <span className="inline-flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {fmt.npr(totalVar)}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card> : null}
      <p className="text-[11px] text-muted-foreground">
        Variance = (declared cash + extra) − cash from orders, plus declared online − online from orders.
        Stores within ±NPR 1 are considered balanced. Use this view operationally; finance still owns the formal GL.
      </p>
    </div>
  );
}

interface SectionProps {
  params: { from: string; to: string; storeId: number | null };
}

function DayCloseSection({ params }: SectionProps) {
  const access = useAccess();
  const canSeeAnyWidget = [
    'reports.day-close.completed-revenue-card',
    'reports.day-close.gross-final-total-card',
    'reports.day-close.total-profit-card',
    'reports.day-close.profit-margin-card',
    'reports.day-close.orders-card',
    'reports.day-close.avg-order-value-card',
    'reports.day-close.delivery-fees-card',
    'reports.day-close.avg-completion-card',
    'reports.day-close.status-breakdown-card',
    'reports.day-close.payment-mix-chart',
    'reports.day-close.store-breakdown-table',
  ].some((slug) => access.can(slug));
  if (!canSeeAnyWidget) return <EmptyState title="No day-close widgets assigned" />;
  return <DayCloseData params={params} />;
}

function DayCloseData({ params }: SectionProps) {
  const access = useAccess();
  const summary = useOrderSummary(params);
  const data = summary.data;

  if (summary.isLoading) {
    return <Skeleton className="h-72 w-full" />;
  }
  if (summary.isError) {
    return <ErrorState onRetry={() => void summary.refetch()} />;
  }
  if (!data) {
    return (
      <EmptyState
        icon={<Inbox className="h-5 w-5" />}
        title="No data for this range"
        description="Pick a different date window or store and we'll re-run the books."
        withTip
      />
    );
  }

  const totals = data.totals;
  const statusEntries = Object.entries(data.status_counts);
  const paymentDonut = [
    { name: 'COD', value: data.payment_mix.cod },
    { name: 'Online', value: data.payment_mix.online },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 stagger md:grid-cols-2 lg:grid-cols-4">
        {access.can('reports.day-close.completed-revenue-card') ? (
          <SummaryStat
            label="Completed revenue"
            value={totals.completed_revenue}
            format={(n) => fmt.npr(n)}
            icon={<Banknote className="h-4 w-4" />}
          />
        ) : null}
        {access.can('reports.day-close.gross-final-total-card') ? (
          <SummaryStat
            label="Gross final total"
            value={totals.gross_final_total}
            format={(n) => fmt.npr(n)}
          />
        ) : null}
        {access.can('reports.day-close.total-profit-card') ? (
          <SummaryStat label="Total profit" value={totals.total_profit} format={(n) => fmt.npr(n)} />
        ) : null}
        {access.can('reports.day-close.profit-margin-card') ? (
          <SummaryStat
            label="Profit margin"
            value={totals.profit_margin_percentage ?? 0}
            format={(n) => `${n.toFixed(1)}%`}
          />
        ) : null}
        {access.can('reports.day-close.orders-card') ? (
          <SummaryStat
            label="Orders"
            value={totals.orders}
            format={(n) => fmt.number(n)}
            icon={<PackageCheck className="h-4 w-4" />}
          />
        ) : null}
        {access.can('reports.day-close.avg-order-value-card') ? (
          <SummaryStat
            label="Avg order value"
            value={totals.avg_order_value}
            format={(n) => fmt.npr(n)}
          />
        ) : null}
        {access.can('reports.day-close.delivery-fees-card') ? (
          <SummaryStat
            label="Delivery fees"
            value={totals.delivery_fee_total}
            format={(n) => fmt.npr(n)}
            icon={<Receipt className="h-4 w-4" />}
          />
        ) : null}
        {access.can('reports.day-close.avg-completion-card') ? (
          <SummaryStat
            label="Avg completion"
            value={(totals.avg_completion_time_minutes ?? 0) * 60}
            format={(n) => fmt.duration(n)}
            icon={<Timer className="h-4 w-4" />}
          />
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {access.can('reports.day-close.status-breakdown-card') ? <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Status breakdown</CardTitle>
            <Badge tone="neutral">
              <AnimatedNumber value={totals.orders} format={(n) => fmt.number(n)} duration={500} /> orders
            </Badge>
          </CardHeader>
          <CardBody>
            <ul className="grid gap-2 sm:grid-cols-2">
              {statusEntries.map(([status, count]) => (
                <li
                  key={status}
                  className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-sm"
                >
                  <span className="text-muted-foreground">{statusLabel(status)}</span>
                  <span className="kpi-num font-medium">
                    <AnimatedNumber value={count} format={(n) => fmt.number(n)} duration={400} />
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card> : null}

        {access.can('reports.day-close.payment-mix-chart') ? <Card>
          <CardHeader>
            <CardTitle>Payment mix</CardTitle>
          </CardHeader>
          <CardBody>
            {paymentDonut.length === 0 ? (
              <EmptyState
                icon={<Inbox className="h-5 w-5" />}
                title="No completed payments yet"
                description="Once orders complete, the COD vs online split shows up here."
              />
            ) : (
              <DonutChart data={paymentDonut} formatValue={(v) => fmt.number(v)} />
            )}
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">COD</span>
              <span className="kpi-num font-medium">
                <AnimatedNumber value={data.payment_mix.cod} format={(n) => fmt.number(n)} duration={400} />
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Online</span>
              <span className="kpi-num font-medium">
                <AnimatedNumber value={data.payment_mix.online} format={(n) => fmt.number(n)} duration={400} />
              </span>
            </div>
          </CardBody>
        </Card> : null}
      </div>

      {access.can('reports.day-close.store-breakdown-table') && data.store_breakdown.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Store breakdown</CardTitle>
          </CardHeader>
          <CardBody className="!pt-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Store</th>
                    <th className="px-3 py-2 text-right font-medium">Orders</th>
                    <th className="px-3 py-2 text-right font-medium">Revenue</th>
                    <th className="px-3 py-2 text-right font-medium">Profit</th>
                    <th className="px-3 py-2 text-right font-medium">Margin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.store_breakdown.map((row) => (
                    <tr key={row.store_id}>
                      <td className="px-3 py-2 font-medium">{row.store_name}</td>
                      <td className="px-3 py-2 text-right kpi-num">{fmt.number(row.orders)}</td>
                      <td className="px-3 py-2 text-right kpi-num">{fmt.npr(row.completed_revenue)}</td>
                      <td className="px-3 py-2 text-right kpi-num">{fmt.npr(row.total_profit)}</td>
                      <td className="px-3 py-2 text-right kpi-num">
                        {(row.profit_margin_percentage ?? 0).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

const PAGE_SIZE = 25;

function SalesRegisterSection({ params }: SectionProps) {
  const access = useAccess();
  if (!access.can('reports.sales-register.table')) {
    return <EmptyState title="No sales-register table assigned" />;
  }
  return <SalesRegisterData params={params} />;
}

function SalesRegisterData({ params }: SectionProps) {
  const access = useAccess();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const sales = useProductSales({ ...params, page, page_size: PAGE_SIZE, search });
  const rows = (sales.data?.results ?? []) as ProductSalesRow[];
  const pagination = sales.data?.pagination;

  // Keep the page within bounds when the search filter shrinks the result set.
  const safePage = pagination ? Math.min(page, Math.max(1, pagination.total_pages)) : page;

  const columns = useMemo<ColumnDef<ProductSalesRow>[]>(
    () => [
      {
        accessorKey: 'product_name',
        header: 'Product',
        cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="min-w-[180px]">
              <p className="truncate font-medium">{r.product_name}</p>
              {r.size || r.measurement ? (
                <p className="text-[11px] text-muted-foreground">
                  {[r.size, r.measurement].filter(Boolean).join(' · ')}
                </p>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: 'subcategory',
        header: 'Subcategory',
        cell: ({ getValue }) => (
          <span className="text-muted-foreground">{(getValue<string>() || '—')}</span>
        ),
      },
      {
        accessorKey: 'quantity',
        header: 'Qty',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.number(getValue<number>())}</span>,
        sortingFn: 'basic',
      },
      {
        accessorKey: 'order_count',
        header: 'Orders',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.number(getValue<number>())}</span>,
        sortingFn: 'basic',
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ getValue }) => (
          <span className="kpi-num font-semibold">{fmt.npr(getValue<number>())}</span>
        ),
        sortingFn: 'basic',
      },
    ],
    [],
  );

  return (
    <DataView
      title="Product sales register"
      description="Per-product totals for the selected window. Search is server-side."
      data={rows}
      columns={columns}
      isLoading={sales.isLoading}
      isError={sales.isError}
      onRetry={() => void sales.refetch()}
      density="compact"
      emptyTitle="No product sales"
      emptyDescription="Try widening the date range or clearing your search."
      search={{
        value: search,
        onChange: (v) => {
          setSearch(v);
          setPage(1);
        },
        placeholder: 'Search products…',
      }}
      pagination={
        pagination
          ? {
              page: safePage,
              pageSize: pagination.page_size ?? PAGE_SIZE,
              total: pagination.count,
              onPageChange: setPage,
            }
          : undefined
      }
      onExportCsv={
        access.can('reports.sales-register.export', 'export')
          ? () =>
              downloadCsv(
                `sales-register-${format(new Date(), 'yyyyMMdd-HHmm')}.csv`,
                rows.map((r) => ({
                  product_id: r.product_id ?? '',
                  product_name: r.product_name,
                  subcategory: r.subcategory,
                  size: r.size,
                  measurement: r.measurement,
                  price: r.price,
                  quantity: r.quantity,
                  order_count: r.order_count,
                  amount: r.amount,
                })),
                [
                  { key: 'product_id', label: 'product_id' },
                  { key: 'product_name', label: 'product' },
                  { key: 'subcategory', label: 'subcategory' },
                  { key: 'size', label: 'size' },
                  { key: 'measurement', label: 'measurement' },
                  { key: 'price', label: 'price' },
                  { key: 'quantity', label: 'quantity' },
                  { key: 'order_count', label: 'orders' },
                  { key: 'amount', label: 'amount' },
                ],
              )
          : undefined
      }
    />
  );
}

interface SummaryStatProps {
  label: string;
  value: number;
  format: (n: number) => string;
  icon?: React.ReactNode;
}

function SummaryStat({ label, value, format, icon }: SummaryStatProps) {
  return (
    <Card>
      <CardBody className="!pt-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {icon ? <span className={cn('text-muted-foreground/80')}>{icon}</span> : null}
          <span>{label}</span>
        </div>
        <p className="mt-2 font-display text-2xl font-bold tracking-tight kpi-num">
          <AnimatedNumber value={value} format={format} duration={500} />
        </p>
      </CardBody>
    </Card>
  );
}
